import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ArrowSmRightIcon, SupportIcon } from "@heroicons/react/outline";
import { PhoneIcon } from "@heroicons/react/solid";
import { CurrentUser } from "@hilos/types/private-schema";
import i18n from "src/i18n";
import { buildRoute } from "src/router/router";
import FlowTemplatesSection from "../FlowTemplatesSection";
import GuidesSection from "../GuidesSection";
import MainDashboardCard from "../MainDashboardCard";

export interface NewDashboardProps {
  session: CurrentUser;
  QRCode: string | null;
  urlWACode: string;
}

export default function NewDashboard({
  session,
  QRCode,
  urlWACode,
}: NewDashboardProps) {
  const { t } = useTranslation();
  const { language } = i18n;

  return (
    <div className="h-screen overflow-y-auto w-auto gap-6 overflow-x-clip grid grid-cols-2 bg-gray-50 p-6">
      <div className=" col-span-2 lg:col-span-2 w-full h-20 flex lg:mt-5">
        <div>
          <Trans i18nKey="dashboard:sandbox.main-title">
            <h2 className="mt-2 flex items-center text-2xl font-bold leading-7 text-neutral-dark  sm:truncate sm:text-3xl">
              Start discovering Hilos!
            </h2>
            <p className="mt-1 text-neutral">
              Take a look at everything Hilos offers to help your company.
            </p>
          </Trans>
        </div>
        <div className="self-center ml-auto shrink-0">
          <Link
            to={buildRoute("config-channel-cloud-api-onboarding")}
            className="z-10 lg:mr-10 inline-flex items-center rounded-md border border-transparent bg-hilos px-4 py-3 text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
          >
            <FontAwesomeIcon
              // @ts-ignore
              icon={faWhatsapp}
              aria-hidden="true"
              className="mr-2 h-5 w-5"
            />
            {t("dashboard:sandbox.connect-number", "Connect your number")}
            <ArrowSmRightIcon className="ml-2 h-5 w-5" aria-hidden="true" />
          </Link>
        </div>
      </div>
      <div className=" col-span-2 lg:col-span-2 mb-6 w-full">
        <MainDashboardCard
          urlWhatsAppCode={urlWACode}
          QRSandboxCode={QRCode}
          session={session}
        />
      </div>
      <div className=" col-span-2 lg:col-span-1 w-full">
        <GuidesSection />
      </div>
      <div className=" flex flex-col col-span-2 lg:col-span-1 w-full gap-6">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <div className="bg-white rounded-md flex items-center p-6 border border-gray-200">
            <div className=" rounded-full h-14 aspect-square flex items-center justify-center bg-hilos-light p-3">
              <SupportIcon className="h-6 w-6 text-hilos-alt-700" />
            </div>
            <div className="flex flex-col space-between ml-6 w-full gap-4">
              <p className="font-semibold">
                {t(
                  "dashboard:sandbox.docs.title",
                  "Everything you need to know"
                )}
              </p>
              <p className=" text-xs text-gray-500">
                {t(
                  "dashboard:sandbox.docs.subtitle",
                  "Check out our documentation to learn how to get the most out of Hilos."
                )}
              </p>
              <div className="flex w-full justify-start">
                <a
                  href="https://hilos.io/docs"
                  target="_blank"
                  rel="noreferrer"
                  className="flex border border-hilos text-hilos-alt-700 px-6 py-2 rounded-md items-center text-xs font-medium"
                >
                  {t("dashboard:sandbox.docs.button", "See documentation")}
                </a>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-md flex items-center p-6 border border-gray-200">
            <div className=" rounded-full h-14 aspect-square flex items-center justify-center bg-hilos-light p-3">
              <PhoneIcon className="h-6 w-6 text-hilos-alt-700" />
            </div>
            <div className="flex flex-col space-between ml-6 w-full gap-4">
              <p className="font-semibold">
                {t("dashboard:sandbox.call.title", "Feeling lost? Contact us!")}
              </p>
              <p className=" text-xs text-gray-500">
                {t(
                  "dashboard:sandbox.call.subtitle",
                  "Our support team is happy to help you with any questions you may have."
                )}
              </p>
              <div className="flex w-full justify-start">
                <a
                  href={
                    language && language.toLowerCase().indexOf("es") === 0
                      ? "https://wa.me/525592257050?text=Hola!%20Necesito%20ayuda%20con%20Hilos."
                      : "https://wa.me/525592257050?text=Hi!%20I%20need%20help%20with%20Hilos."
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="flex border border-hilos text-hilos-alt-700 px-6 py-2 rounded-md items-center text-xs font-medium"
                >
                  {t("dashboard:support.button")}
                </a>
              </div>
            </div>
          </div>
        </div>
        <FlowTemplatesSection />
      </div>
    </div>
  );
}
